import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { companyTeamApi } from '../lib/api';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import {
    Users, Plus, Trash2, Mail, Building2, Loader2, Shield, AlertTriangle,
    ChevronDown, ChevronRight, Bell,
} from 'lucide-react';

const ROLE_BADGE = {
    owner: 'bg-amber-100 text-amber-800',
    admin: 'bg-indigo-100 text-indigo-700',
    member: 'bg-slate-100 text-slate-700',
    viewer: 'bg-slate-100 text-slate-500',
    customer: 'bg-slate-100 text-slate-700',
    super_admin: 'bg-red-100 text-red-700',
};

const NOTIFICATION_DEFINITIONS = [
    { key: 'production_confirmed', label: 'Production confirmed', tKey: 'team.notif.production_confirmed' },
    { key: 'delivery_notice', label: 'Shipping / delivery notice', tKey: 'team.notif.delivery_notice' },
    { key: 'invoice_email', label: 'Invoice', tKey: 'team.notif.invoice_email' },
    { key: 'artwork_revision_requested', label: 'Artwork revision requested', tKey: 'team.notif.artwork_revision_requested' },
    { key: 'artwork_ready_for_approval', label: 'Artwork ready for approval', tKey: 'team.notif.artwork_ready_for_approval' },
    { key: 'upload_link', label: 'Upload link', tKey: 'team.notif.upload_link' },
    { key: 'order_updates', label: 'Order updates', tKey: 'team.notif.order_updates' },
];

// Mirrors backend MANDATORY_NOTIFICATION_KEYS — these are action-required
// transactional emails (customer must respond) and CANNOT be turned off.
const MANDATORY_NOTIFICATION_KEYS = new Set([
    'artwork_revision_requested',
    'artwork_ready_for_approval',
]);

export default function CompanyTeamPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user: currentUser } = useAuth();
    const { t } = useTranslation();

    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState(searchParams.get('company') || '');
    const [team, setTeam] = useState(null);
    const [isLoadingTeam, setIsLoadingTeam] = useState(false);
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);

    // Invite dialog
    const [showInvite, setShowInvite] = useState(false);
    const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'member' });
    const [isInviting, setIsInviting] = useState(false);

    // Per-row state
    const [rolePending, setRolePending] = useState({}); // user_id -> bool
    const [removingId, setRemovingId] = useState(null);

    // Notification expansion
    const [expandedUserId, setExpandedUserId] = useState(null);
    const [notifEdits, setNotifEdits] = useState({}); // user_id -> { key: bool }
    const [savingNotif, setSavingNotif] = useState(null);

    const actorRole = team?.actor_role;
    const canManage = !!team?.can_manage;
    const isOwnerOrSuper = actorRole === 'owner' || actorRole === 'super_admin';

    // ==================== Data loading ====================

    const loadCompanies = useCallback(async () => {
        setIsLoadingCompanies(true);
        try {
            const { data } = await companyTeamApi.getManageableCompanies();
            setCompanies(data || []);
            if (!selectedCompanyId && data && data.length > 0) {
                setSelectedCompanyId(data[0].company_id);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || t('team.load_companies_error', 'Failed to load your companies'));
        } finally {
            setIsLoadingCompanies(false);
        }
    }, [selectedCompanyId]);

    const loadTeam = useCallback(async (companyId) => {
        if (!companyId) return;
        setIsLoadingTeam(true);
        try {
            const { data } = await companyTeamApi.getTeam(companyId);
            setTeam(data);
            // Snapshot prefs into edits
            const edits = {};
            (data.members || []).forEach(m => {
                edits[m.user_id] = { ...(m.notification_preferences || {}) };
            });
            setNotifEdits(edits);
        } catch (err) {
            toast.error(err.response?.data?.detail || t('team.load_team_error', 'Failed to load team'));
            setTeam(null);
        } finally {
            setIsLoadingTeam(false);
        }
    }, []);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    useEffect(() => {
        if (selectedCompanyId) {
            setSearchParams({ company: selectedCompanyId }, { replace: true });
            loadTeam(selectedCompanyId);
        }
    }, [selectedCompanyId, loadTeam, setSearchParams]);

    // ==================== Actions ====================

    const handleInvite = async () => {
        if (!inviteForm.email) {
            toast.error(t('team.email_required', 'Email is required'));
            return;
        }
        setIsInviting(true);
        try {
            await companyTeamApi.invite(selectedCompanyId, {
                email: inviteForm.email.trim(),
                name: inviteForm.name.trim() || undefined,
                role: inviteForm.role,
            });
            toast.success(t('team.invited_toast', 'Invited {email}. Magic-link email sent.', { email: inviteForm.email }));
            setShowInvite(false);
            setInviteForm({ email: '', name: '', role: 'member' });
            loadTeam(selectedCompanyId);
        } catch (err) {
            toast.error(err.response?.data?.detail || t('team.invite_error', 'Failed to invite'));
        } finally {
            setIsInviting(false);
        }
    };

    const handleRoleChange = async (member, newRole) => {
        if (newRole === member.role) return;
        setRolePending((p) => ({ ...p, [member.user_id]: true }));
        try {
            await companyTeamApi.updateRole(selectedCompanyId, member.user_id, newRole);
            toast.success(t('team.role_updated_toast', "Updated {name}'s role to {role}", { name: member.name, role: newRole }));
            loadTeam(selectedCompanyId);
        } catch (err) {
            toast.error(err.response?.data?.detail || t('team.role_update_error', 'Failed to update role'));
        } finally {
            setRolePending((p) => ({ ...p, [member.user_id]: false }));
        }
    };

    const handleRemove = async (member) => {
        if (!window.confirm(t('team.remove_confirm', 'Remove {name} from {company}?', { name: member.name, company: team.company_name }))) return;
        setRemovingId(member.user_id);
        try {
            await companyTeamApi.remove(selectedCompanyId, member.user_id);
            toast.success(t('team.removed_toast', 'Removed {name}', { name: member.name }));
            loadTeam(selectedCompanyId);
        } catch (err) {
            toast.error(err.response?.data?.detail || t('team.remove_error', 'Failed to remove member'));
        } finally {
            setRemovingId(null);
        }
    };

    const toggleNotif = (userId, key, value) => {
        // Block disabling the LAST recipient for a mandatory key
        if (
            MANDATORY_NOTIFICATION_KEYS.has(key) &&
            value === false &&
            isLastRecipient(userId, key)
        ) {
            toast.error(
                t(
                    'team.mandatory_notif_error',
                    'At least one company member must receive artwork approval and revision emails. Enable it for another teammate first.'
                )
            );
            return;
        }
        setNotifEdits((prev) => ({
            ...prev,
            [userId]: { ...(prev[userId] || {}), [key]: value },
        }));
    };

    const isLastRecipient = (userId, key) => {
        if (!MANDATORY_NOTIFICATION_KEYS.has(key)) return false;
        const original = team?.members?.find(m => m.user_id === userId)?.notification_preferences || {};
        const wasEnabled = Boolean(original[key]);
        if (!wasEnabled) return false;
        const count = team?.notification_recipient_counts?.[key];
        return Number.isFinite(count) && count <= 1;
    };

    const handleSaveNotifs = async (member) => {
        setSavingNotif(member.user_id);
        try {
            await companyTeamApi.updateNotifications(
                selectedCompanyId,
                member.user_id,
                notifEdits[member.user_id] || {}
            );
            toast.success(t('team.prefs_saved_toast', 'Saved preferences for {name}', { name: member.name }));
            loadTeam(selectedCompanyId);
        } catch (err) {
            toast.error(err.response?.data?.detail || t('team.prefs_save_error', 'Failed to save preferences'));
        } finally {
            setSavingNotif(null);
        }
    };

    const allowedAssignableRoles = useMemo(() => {
        if (isOwnerOrSuper) return ['owner', 'admin', 'member', 'viewer'];
        if (actorRole === 'admin') return ['admin', 'member', 'viewer'];
        return [];
    }, [actorRole, isOwnerOrSuper]);

    const isPrefsDirty = (userId) => {
        const original = team?.members?.find(m => m.user_id === userId)?.notification_preferences || {};
        const edited = notifEdits[userId] || {};
        return NOTIFICATION_DEFINITIONS.some(({ key }) => Boolean(original[key]) !== Boolean(edited[key]));
    };

    // ==================== Render ====================

    if (isLoadingCompanies) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center py-20"><LoadingSpinner /></div>
            </div>
        );
    }

    if (companies.length === 0) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <main className="max-w-3xl mx-auto px-6 py-10">
                    <div className="bg-white rounded-lg border p-8 text-center">
                        <Shield className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                        <h2 className="text-lg font-semibold text-slate-700 mb-1">{t('team.no_companies_title', 'No companies to manage')}</h2>
                        <p className="text-sm text-slate-500">
                            {t('team.no_companies_desc', 'You currently are not an owner or admin of any company. Ask a company owner to grant you access.')}
                        </p>
                        <Button onClick={() => navigate('/dashboard')} className="mt-4" variant="outline">
                            {t('team.back_to_dashboard', 'Back to Dashboard')}
                        </Button>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="max-w-5xl mx-auto px-6 py-6">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <Users className="h-6 w-6 text-indigo-600" />
                            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
                                {t('team.title', 'Team')}
                            </h1>
                        </div>
                        <p className="text-sm text-slate-500">
                            {t('team.subtitle', 'Manage who has access to your company and their email notifications.')}
                        </p>
                    </div>
                    {canManage && (
                        <Button
                            onClick={() => setShowInvite(true)}
                            className="bg-indigo-600 hover:bg-indigo-700"
                            data-testid="invite-team-member-btn"
                        >
                            <Plus className="h-4 w-4 mr-1" />
                            {t('team.invite_member', 'Invite member')}
                        </Button>
                    )}
                </div>

                {/* Company picker */}
                {companies.length > 1 && (
                    <div className="mb-4 flex items-center gap-3">
                        <Building2 className="h-5 w-5 text-slate-400" />
                        <Label className="text-sm font-medium text-slate-700">{t('team.company_label', 'Company:')}</Label>
                        <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                            <SelectTrigger className="w-[280px]" data-testid="company-picker">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {companies.map((c) => (
                                    <SelectItem key={c.company_id} value={c.company_id}>
                                        {c.company_name}
                                        <span className="ml-2 text-xs text-slate-500">({c.role})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {isLoadingTeam ? (
                    <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
                ) : team && (
                    <div className="bg-white rounded-lg border border-border shadow-premium-sm overflow-hidden">
                        <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between">
                            <div>
                                <h2 className="text-sm font-semibold text-slate-700">{team.company_name}</h2>
                                <p className="text-xs text-slate-500">
                                    {team.members.length} {team.members.length === 1 ? t('team.member_singular', 'member') : t('team.member_plural', 'members')} • {t('team.your_role', 'Your role:')} <span className="font-medium capitalize">{actorRole}</span>
                                </p>
                            </div>
                            {!canManage && (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                                    <AlertTriangle className="h-3.5 w-3.5" />
                                    {t('team.read_only', 'Read-only access')}
                                </span>
                            )}
                        </div>

                        <div className="divide-y">
                            {team.members.map((m) => {
                                const isExpanded = expandedUserId === m.user_id;
                                const isSelf = m.user_id === currentUser?.id;
                                const dirty = isPrefsDirty(m.user_id);
                                const canEditPrefs = canManage || isSelf;
                                const canEditRole = canManage && !isSelf && allowedAssignableRoles.length > 0
                                    && (m.role !== 'owner' || isOwnerOrSuper);
                                const canRemove = canManage && !isSelf
                                    && (m.role !== 'owner' || isOwnerOrSuper);

                                return (
                                    <div key={m.user_id} data-testid={`team-row-${m.user_id}`}>
                                        <div className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50">
                                            {/* Avatar */}
                                            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600 flex-shrink-0">
                                                {m.name?.charAt(0).toUpperCase() || '?'}
                                            </div>

                                            {/* Identity */}
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-medium text-slate-800 text-sm truncate">{m.name}</span>
                                                    {isSelf && (
                                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                                            {t('team.you_badge', 'You')}
                                                        </span>
                                                    )}
                                                    {!m.is_active && (
                                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                                                            {t('team.disabled_badge', 'Disabled')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                                    <Mail className="h-3 w-3" />
                                                    <span className="truncate">{m.email}</span>
                                                </div>
                                            </div>

                                            {/* Role */}
                                            <div className="flex items-center gap-2">
                                                {canEditRole ? (
                                                    <Select
                                                        value={m.role}
                                                        onValueChange={(v) => handleRoleChange(m, v)}
                                                        disabled={rolePending[m.user_id]}
                                                    >
                                                        <SelectTrigger className="w-[130px] h-8" data-testid={`role-select-${m.user_id}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {allowedAssignableRoles.map((r) => (
                                                                <SelectItem key={r} value={r}>
                                                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                                                </SelectItem>
                                                            ))}
                                                            {/* Allow seeing legacy 'customer' role */}
                                                            {m.role === 'customer' && (
                                                                <SelectItem value="customer">{t('team.role_customer_legacy', 'Customer (legacy)')}</SelectItem>
                                                            )}
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <span className={`px-2 py-1 rounded text-[11px] font-medium uppercase tracking-wide ${ROLE_BADGE[m.role] || 'bg-slate-100 text-slate-600'}`}>
                                                        {m.role}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Notif expand toggle */}
                                            {canEditPrefs && (
                                                <button
                                                    onClick={() => setExpandedUserId(isExpanded ? null : m.user_id)}
                                                    className="p-1.5 text-slate-400 hover:text-indigo-600"
                                                    title={t('team.email_notif_title', 'Email notifications')}
                                                    data-testid={`expand-notif-${m.user_id}`}
                                                >
                                                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                </button>
                                            )}

                                            {/* Remove */}
                                            {canRemove && (
                                                <button
                                                    onClick={() => handleRemove(m)}
                                                    disabled={removingId === m.user_id}
                                                    className="p-1.5 text-slate-400 hover:text-red-600 disabled:opacity-50"
                                                    title={t('team.remove_title', 'Remove from company')}
                                                    data-testid={`remove-${m.user_id}`}
                                                >
                                                    {removingId === m.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                                </button>
                                            )}
                                        </div>

                                        {/* Expanded notification preferences */}
                                        {isExpanded && canEditPrefs && (
                                            <div className="px-5 py-4 bg-slate-50 border-t border-b border-slate-200">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                                        <Bell className="h-4 w-4" />
                                                        {t('team.email_notif_for', 'Email notifications for {name}', { name: m.name })}
                                                    </div>
                                                </div>
                                                <div
                                                    className="mb-3 px-3 py-2 rounded-md bg-indigo-50 border border-indigo-100 text-xs text-indigo-900"
                                                    data-testid={`team-notif-mandatory-info-${m.user_id}`}
                                                >
                                                    {t('team.mandatory_info', 'At least one company member must receive artwork approval and revision emails.')}
                                                </div>
                                                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                                                    {NOTIFICATION_DEFINITIONS.map(({ key, label, tKey }) => {
                                                        const isMandatory = MANDATORY_NOTIFICATION_KEYS.has(key);
                                                        const lastRecipient = isLastRecipient(m.user_id, key);
                                                        const editedValue = Boolean((notifEdits[m.user_id] || {})[key]);
                                                        const disableToggle = isMandatory && lastRecipient && editedValue;
                                                        return (
                                                            <div key={key} className="flex items-center justify-between py-1.5">
                                                                <span className="text-sm text-slate-600 flex items-center gap-1.5">
                                                                    {t(tKey, label)}
                                                                    {disableToggle && (
                                                                        <span
                                                                            className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                                                                            data-testid={`team-last-recipient-${key}`}
                                                                            title={t('team.last_recipient_title', "You're the last enabled recipient — at least one company member must receive these emails.")}
                                                                        >
                                                                            {t('team.last_badge', 'Last')}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                                <Switch
                                                                    checked={editedValue}
                                                                    disabled={disableToggle}
                                                                    onCheckedChange={(v) => toggleNotif(m.user_id, key, v)}
                                                                    data-testid={`notif-${m.user_id}-${key}`}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <div className="flex justify-end items-center gap-3 mt-3 pt-3 border-t">
                                                    {dirty && <span className="text-xs text-amber-600">{t('team.unsaved', 'Unsaved')}</span>}
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleSaveNotifs(m)}
                                                        disabled={!dirty || savingNotif === m.user_id}
                                                        className="bg-indigo-600 hover:bg-indigo-700"
                                                        data-testid={`save-notif-${m.user_id}`}
                                                    >
                                                        {savingNotif === m.user_id ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </main>

            {/* Invite Dialog */}
            <Dialog open={showInvite} onOpenChange={setShowInvite}>
                <DialogContent className="sm:max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Plus className="h-5 w-5 text-indigo-600" />
                            {t('team.invite_dialog_title', 'Invite team member')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('team.invite_dialog_desc', "We'll email a magic-link sign-in to {email} so they can join {company}.", { email: inviteForm.email || t('team.invite_email_fallback', 'them'), company: team?.company_name || t('team.invite_company_fallback', 'your company') })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>{t('team.invite_email_label', 'Email')} <span className="text-red-500">*</span></Label>
                            <Input
                                type="email"
                                placeholder="user@example.com"
                                value={inviteForm.email}
                                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                                data-testid="invite-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('team.invite_name_label', 'Display name')} <span className="text-slate-400 text-xs">{t('team.optional', '(optional)')}</span></Label>
                            <Input
                                placeholder={t('team.invite_name_placeholder', 'Will derive from email if blank')}
                                value={inviteForm.name}
                                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                                data-testid="invite-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{t('team.invite_role_label', 'Role')}</Label>
                            <Select
                                value={inviteForm.role}
                                onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
                            >
                                <SelectTrigger data-testid="invite-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {allowedAssignableRoles.map((r) => (
                                        <SelectItem key={r} value={r}>
                                            {r.charAt(0).toUpperCase() + r.slice(1)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                {inviteForm.role === 'owner' && t('team.role_desc_owner', 'Full control over the company. Can promote others to owner.')}
                                {inviteForm.role === 'admin' && t('team.role_desc_admin', 'Can manage orders and team (except owners).')}
                                {inviteForm.role === 'member' && t('team.role_desc_member', 'Day-to-day user. Receives most notifications.')}
                                {inviteForm.role === 'viewer' && t('team.role_desc_viewer', 'Read-only access. No email notifications by default.')}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowInvite(false)}>{t('common.cancel', 'Cancel')}</Button>
                        <Button
                            onClick={handleInvite}
                            disabled={isInviting}
                            className="bg-indigo-600 hover:bg-indigo-700"
                            data-testid="confirm-invite-btn"
                        >
                            {isInviting ? t('team.inviting', 'Inviting...') : t('team.send_invite', 'Send invite')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
